// componentLibrary.js - RF Component Definitions
import { injectDataSourceParameters } from './dataSourceParameters';

export const COMPONENT_LIBRARY = {
  // ============================================================================
  // SIGNAL SOURCES
  // ============================================================================

  inputSource: {
    type: 'inputSource',
    displayName: 'Signal Generator',
    category: 'sources',
    inputs: [],
    outputs: ['rfOut'],
    parameters: {
      frequency: {
        name: 'Frequency',
        value: 1700,
        unit: 'MHz',
        min: 0.1,
        max: 100000,
        tracked: true,
        displayOnCard: true,
        description: 'Operating frequency'
      },
      power: {
        name: 'Power',
        value: -10,
        unit: 'dBm',
        min: -150,
        max: 50,
        tracked: true,
        displayOnCard: true,
        description: 'Output power'
      },
      temperature: {
        name: 'Temperature',
        value: 25,
        unit: '°C',
        min: -273,
        max: 200,
        tracked: true,
        displayOnCard: false,
        description: 'Operating temperature'
      },
      impedance: {
        name: 'Impedance',
        value: 50,
        unit: 'Ω',
        min: 1,
        max: 1000,
        tracked: true,
        displayOnCard: false,
        description: 'Source impedance'
      }
    }
  },

  noiseSource: {
    type: 'noiseSource',
    displayName: 'Noise Source',
    category: 'sources',
    inputs: [],
    outputs: ['rfOut'],
    parameters: {
      enr: {
        name: 'ENR',
        value: 15,
        unit: 'dB',
        min: 0,
        max: 50,
        tracked: true,
        displayOnCard: true,
        description: 'Excess Noise Ratio'
      },
      temperature: {
        name: 'Temperature',
        value: 25,
        unit: '°C',
        tracked: true,
        displayOnCard: false
      }
    }
  },

  // ============================================================================
  // AMPLIFIERS
  // ============================================================================

  lna: {
    type: 'lna',
    displayName: 'LNA',
    category: 'amplifiers',
    inputs: ['rfIn'],
    outputs: ['rfOut'],
    parameters: {
      gain: {
        name: 'Gain',
        value: 20,
        unit: 'dB',
        min: -50,
        max: 50,
        tracked: true,
        displayOnCard: true,
        description: 'Small signal gain (S21)'
      },
      noiseFigure: {
        name: 'NF',
        value: 1.5,
        unit: 'dB',
        min: 0,
        max: 30,
        tracked: true,
        displayOnCard: true,
        description: 'Noise figure'
      },
      p1db: {
        name: 'OP1dB',
        value: 15,
        unit: 'dBm',
        min: -50,
        max: 60,
        tracked: true,
        displayOnCard: true,
        description: '1dB compression point (output)'
      },
      oip3: {
        name: 'OIP3',
        value: 25,
        unit: 'dBm',
        min: -50,
        max: 70,
        tracked: true,
        displayOnCard: true,
        description: 'Output 3rd-order intercept'
      },
      s11: {
        name: 'S11',
        value: -15,
        unit: 'dB',
        min: -60,
        max: 0,
        tracked: true,
        displayOnCard: false,
        description: 'Input return loss'
      },
      s22: {
        name: 'S22',
        value: -15,
        unit: 'dB',
        min: -60,
        max: 0,
        tracked: true,
        displayOnCard: false,
        description: 'Output return loss'
      },
      frequencyMin: {
        name: 'Freq Min',
        value: 100,
        unit: 'MHz',
        min: 0.01,
        max: 100000,
        tracked: true,
        displayOnCard: false,
        description: 'Minimum operating frequency'
      },
      frequencyMax: {
        name: 'Freq Max',
        value: 6000,
        unit: 'MHz',
        min: 0.01,
        max: 100000,
        tracked: true,
        displayOnCard: false,
        description: 'Maximum operating frequency'
      },
      supplyVoltage: {
        name: 'Supply Voltage',
        value: 5.0,
        unit: 'V',
        min: 0,
        max: 50,
        tracked: true,
        displayOnCard: false,
        description: 'Nominal supply voltage'
      },
      supplyVoltageMin: {
        name: 'Vdd Min',
        value: null,
        unit: 'V',
        min: 0,
        max: 50,
        tracked: true,
        displayOnCard: false,
        description: 'Minimum supply voltage'
      },
      supplyVoltageMax: {
        name: 'Vdd Max',
        value: null,
        unit: 'V',
        min: 0,
        max: 50,
        tracked: true,
        displayOnCard: false,
        description: 'Maximum supply voltage'
      },
      idleCurrent: {
        name: 'Idle Current',
        value: 50,
        unit: 'mA',
        min: 0,
        max: 5000,
        tracked: true,
        displayOnCard: false,
        description: 'Typical quiescent current'
      },
      idleCurrentMin: {
        name: 'Idd Min',
        value: null,
        unit: 'mA',
        min: 0,
        max: 5000,
        tracked: true,
        displayOnCard: false,
        description: 'Minimum idle current'
      },
      idleCurrentMax: {
        name: 'Idd Max',
        value: null,
        unit: 'mA',
        min: 0,
        max: 5000,
        tracked: true,
        displayOnCard: false,
        description: 'Maximum idle current'
      },
      leakageCurrent: {
        name: 'Leakage Current',
        value: null,
        unit: 'µA',
        min: 0,
        max: 1000,
        tracked: true,
        displayOnCard: false,
        description: 'Shutdown/standby leakage current'
      },
      package: {
        name: 'Package',
        value: '',
        unit: '',
        tracked: true,
        displayOnCard: false,
        description: 'Physical package type'
      },
      technology: {
        name: 'Technology',
        value: '',
        unit: '',
        tracked: true,
        displayOnCard: false,
        description: 'Semiconductor process technology'
      },
      dcPower: {
        name: 'DC Power',
        value: 100,
        unit: 'mW',
        min: 0,
        max: 10000,
        tracked: true,
        displayOnCard: false,
        description: 'DC power consumption'
      }
    }
  },

  powerAmplifier: {
    type: 'powerAmplifier',
    displayName: 'Power Amplifier',
    category: 'amplifiers',
    inputs: ['rfIn'],
    outputs: ['rfOut'],
    parameters: {
      gain: {
        name: 'Gain',
        value: 20,
        unit: 'dB',
        min: 0,
        max: 50,
        tracked: true,
        displayOnCard: true
      },
      noiseFigure: {
        name: 'NF',
        value: 4.5,
        unit: 'dB',
        min: 0,
        max: 30,
        tracked: true,
        displayOnCard: true
      },
      p1db: {
        name: 'OP1dB',
        value: 27,
        unit: 'dBm',
        min: 0,
        max: 60,
        tracked: true,
        displayOnCard: true
      },
      oip3: {
        name: 'OIP3',
        value: 38,
        unit: 'dBm',
        min: 0,
        max: 70,
        tracked: true,
        displayOnCard: true
      },
      psat: {
        name: 'Psat',
        value: 43,
        unit: 'dBm',
        min: 0,
        max: 60,
        tracked: true,
        displayOnCard: false,
        description: 'Saturated output power'
      },
      aclr: {
        name: 'ACLR',
        value: null,
        unit: 'dBc',
        min: -100,
        max: 0,
        tracked: true,
        displayOnCard: false,
        description: 'Adjacent Channel Leakage Ratio'
      },
      efficiency: {
        name: 'Efficiency',
        value: 50,
        unit: '%',
        min: 0,
        max: 100,
        tracked: true,
        displayOnCard: false,
        description: 'Power-added efficiency'
      },
      frequencyMin: {
        name: 'Freq Min',
        value: 100,
        unit: 'MHz',
        min: 0.01,
        max: 100000,
        tracked: true,
        displayOnCard: false,
        description: 'Minimum operating frequency'
      },
      frequencyMax: {
        name: 'Freq Max',
        value: 6000,
        unit: 'MHz',
        min: 0.01,
        max: 100000,
        tracked: true,
        displayOnCard: false,
        description: 'Maximum operating frequency'
      },
      supplyVoltage: {
        name: 'Supply Voltage',
        value: 5.0,
        unit: 'V',
        min: 0,
        max: 50,
        tracked: true,
        displayOnCard: false,
        description: 'Nominal supply voltage'
      },
      supplyVoltageMin: {
        name: 'Vdd Min',
        value: null,
        unit: 'V',
        min: 0,
        max: 50,
        tracked: true,
        displayOnCard: false,
        description: 'Minimum supply voltage'
      },
      supplyVoltageMax: {
        name: 'Vdd Max',
        value: null,
        unit: 'V',
        min: 0,
        max: 50,
        tracked: true,
        displayOnCard: false,
        description: 'Maximum supply voltage'
      },
      idleCurrent: {
        name: 'Idle Current',
        value: 200,
        unit: 'mA',
        min: 0,
        max: 5000,
        tracked: true,
        displayOnCard: false,
        description: 'Typical quiescent current'
      },
      idleCurrentMin: {
        name: 'Idd Min',
        value: null,
        unit: 'mA',
        min: 0,
        max: 5000,
        tracked: true,
        displayOnCard: false,
        description: 'Minimum idle current'
      },
      idleCurrentMax: {
        name: 'Idd Max',
        value: null,
        unit: 'mA',
        min: 0,
        max: 5000,
        tracked: true,
        displayOnCard: false,
        description: 'Maximum idle current'
      },
      leakageCurrent: {
        name: 'Leakage Current',
        value: null,
        unit: 'µA',
        min: 0,
        max: 1000,
        tracked: true,
        displayOnCard: false,
        description: 'Shutdown/standby leakage current'
      },
      package: {
        name: 'Package',
        value: '',
        unit: '',
        tracked: true,
        displayOnCard: false,
        description: 'Physical package type'
      },
      technology: {
        name: 'Technology',
        value: '',
        unit: '',
        tracked: true,
        displayOnCard: false,
        description: 'Semiconductor process technology'
      },
      dcPower: {
        name: 'DC Power',
        value: 5000,
        unit: 'mW',
        min: 0,
        max: 100000,
        tracked: true,
        displayOnCard: false
      }
    }
  },

  vga: {
    type: 'vga',
    displayName: 'VGA',
    category: 'amplifiers',
    inputs: ['rfIn'],
    outputs: ['rfOut'],
    parameters: {
      gainMin: {
        name: 'Gain Min',
        value: 0,
        unit: 'dB',
        tracked: true,
        displayOnCard: false
      },
      gainMax: {
        name: 'Gain Max',
        value: 30,
        unit: 'dB',
        tracked: true,
        displayOnCard: false
      },
      gain: {
        name: 'Gain',
        value: 15,
        unit: 'dB',
        min: 0,
        max: 30,
        tracked: true,
        displayOnCard: true
      },
      noiseFigure: {
        name: 'NF',
        value: 6,
        unit: 'dB',
        tracked: true,
        displayOnCard: true
      },
      p1db: {
        name: 'OP1dB',
        value: 20,
        unit: 'dBm',
        tracked: true,
        displayOnCard: true
      },
      oip3: {
        name: 'OIP3',
        value: 30,
        unit: 'dBm',
        tracked: true,
        displayOnCard: false
      }
    }
  },

  gainBlock: {
    type: 'gainBlock',
    displayName: 'Gain Block',
    category: 'amplifiers',
    inputs: ['rfIn'],
    outputs: ['rfOut'],
    parameters: {
      gain: {
        name: 'Gain',
        value: 15,
        unit: 'dB',
        min: -10,
        max: 50,
        tracked: true,
        displayOnCard: true,
        description: 'Fixed small signal gain'
      },
      noiseFigure: {
        name: 'NF',
        value: 5,
        unit: 'dB',
        min: 0,
        max: 20,
        tracked: true,
        displayOnCard: true,
        description: 'Noise figure'
      },
      p1db: {
        name: 'OP1dB',
        value: 17,
        unit: 'dBm',
        min: -10,
        max: 60,
        tracked: true,
        displayOnCard: true,
        description: '1dB compression point (output)'
      },
      oip3: {
        name: 'OIP3',
        value: 27,
        unit: 'dBm',
        min: 0,
        max: 70,
        tracked: true,
        displayOnCard: true,
        description: 'Output 3rd-order intercept'
      },
      s11: {
        name: 'S11',
        value: -15,
        unit: 'dB',
        min: -60,
        max: 0,
        tracked: true,
        displayOnCard: false,
        description: 'Input return loss (internally matched)'
      },
      s22: {
        name: 'S22',
        value: -15,
        unit: 'dB',
        min: -60,
        max: 0,
        tracked: true,
        displayOnCard: false,
        description: 'Output return loss (internally matched)'
      },
      frequencyMin: {
        name: 'Freq Min',
        value: 100,
        unit: 'MHz',
        min: 0.01,
        max: 100000,
        tracked: true,
        displayOnCard: false,
        description: 'Minimum operating frequency'
      },
      frequencyMax: {
        name: 'Freq Max',
        value: 6000,
        unit: 'MHz',
        min: 0.01,
        max: 100000,
        tracked: true,
        displayOnCard: false,
        description: 'Maximum operating frequency'
      },
      supplyVoltage: {
        name: 'Supply Voltage',
        value: 5.0,
        unit: 'V',
        min: 0,
        max: 50,
        tracked: true,
        displayOnCard: false,
        description: 'Nominal supply voltage'
      },
      supplyVoltageMin: {
        name: 'Vdd Min',
        value: null,
        unit: 'V',
        min: 0,
        max: 50,
        tracked: true,
        displayOnCard: false,
        description: 'Minimum supply voltage'
      },
      supplyVoltageMax: {
        name: 'Vdd Max',
        value: null,
        unit: 'V',
        min: 0,
        max: 50,
        tracked: true,
        displayOnCard: false,
        description: 'Maximum supply voltage'
      },
      idleCurrent: {
        name: 'Idle Current',
        value: 100,
        unit: 'mA',
        min: 0,
        max: 5000,
        tracked: true,
        displayOnCard: false,
        description: 'Typical quiescent current'
      },
      idleCurrentMin: {
        name: 'Idd Min',
        value: null,
        unit: 'mA',
        min: 0,
        max: 5000,
        tracked: true,
        displayOnCard: false,
        description: 'Minimum idle current'
      },
      idleCurrentMax: {
        name: 'Idd Max',
        value: null,
        unit: 'mA',
        min: 0,
        max: 5000,
        tracked: true,
        displayOnCard: false,
        description: 'Maximum idle current'
      },
      leakageCurrent: {
        name: 'Leakage Current',
        value: null,
        unit: 'µA',
        min: 0,
        max: 1000,
        tracked: true,
        displayOnCard: false,
        description: 'Shutdown/standby leakage current'
      },
      package: {
        name: 'Package',
        value: '',
        unit: '',
        tracked: true,
        displayOnCard: false,
        description: 'Physical package type'
      },
      technology: {
        name: 'Technology',
        value: '',
        unit: '',
        tracked: true,
        displayOnCard: false,
        description: 'Semiconductor process technology'
      },
      dcPower: {
        name: 'DC Power',
        value: 200,
        unit: 'mW',
        min: 0,
        max: 5000,
        tracked: true,
        displayOnCard: false,
        description: 'DC power consumption'
      }
    }
  },

  driver: {
    type: 'driver',
    displayName: 'Driver',
    category: 'amplifiers',
    inputs: ['rfIn'],
    outputs: ['rfOut'],
    parameters: {
      gain: {
        name: 'Gain',
        value: 20,
        unit: 'dB',
        min: 0,
        max: 50,
        tracked: true,
        displayOnCard: true,
        description: 'Small signal gain'
      },
      noiseFigure: {
        name: 'NF',
        value: 6,
        unit: 'dB',
        min: 0,
        max: 20,
        tracked: true,
        displayOnCard: true,
        description: 'Noise figure'
      },
      p1db: {
        name: 'OP1dB',
        value: 27,
        unit: 'dBm',
        min: 10,
        max: 60,
        tracked: true,
        displayOnCard: true,
        description: '1dB compression point (output)'
      },
      oip3: {
        name: 'OIP3',
        value: 37,
        unit: 'dBm',
        min: 20,
        max: 70,
        tracked: true,
        displayOnCard: true,
        description: 'Output 3rd-order intercept'
      },
      psat: {
        name: 'Psat',
        value: 30,
        unit: 'dBm',
        min: 10,
        max: 60,
        tracked: true,
        displayOnCard: false,
        description: 'Saturated output power'
      },
      aclr: {
        name: 'ACLR',
        value: null,
        unit: 'dBc',
        min: -100,
        max: 0,
        tracked: true,
        displayOnCard: false,
        description: 'Adjacent Channel Leakage Ratio'
      },
      efficiency: {
        name: 'Efficiency',
        value: 40,
        unit: '%',
        min: 0,
        max: 100,
        tracked: true,
        displayOnCard: false,
        description: 'Power-added efficiency'
      },
      frequencyMin: {
        name: 'Freq Min',
        value: 100,
        unit: 'MHz',
        min: 0.01,
        max: 100000,
        tracked: true,
        displayOnCard: false,
        description: 'Minimum operating frequency'
      },
      frequencyMax: {
        name: 'Freq Max',
        value: 6000,
        unit: 'MHz',
        min: 0.01,
        max: 100000,
        tracked: true,
        displayOnCard: false,
        description: 'Maximum operating frequency'
      },
      supplyVoltage: {
        name: 'Supply Voltage',
        value: 5.0,
        unit: 'V',
        min: 0,
        max: 50,
        tracked: true,
        displayOnCard: false,
        description: 'Nominal supply voltage'
      },
      supplyVoltageMin: {
        name: 'Vdd Min',
        value: null,
        unit: 'V',
        min: 0,
        max: 50,
        tracked: true,
        displayOnCard: false,
        description: 'Minimum supply voltage'
      },
      supplyVoltageMax: {
        name: 'Vdd Max',
        value: null,
        unit: 'V',
        min: 0,
        max: 50,
        tracked: true,
        displayOnCard: false,
        description: 'Maximum supply voltage'
      },
      idleCurrent: {
        name: 'Idle Current',
        value: 150,
        unit: 'mA',
        min: 0,
        max: 5000,
        tracked: true,
        displayOnCard: false,
        description: 'Typical quiescent current'
      },
      idleCurrentMin: {
        name: 'Idd Min',
        value: null,
        unit: 'mA',
        min: 0,
        max: 5000,
        tracked: true,
        displayOnCard: false,
        description: 'Minimum idle current'
      },
      idleCurrentMax: {
        name: 'Idd Max',
        value: null,
        unit: 'mA',
        min: 0,
        max: 5000,
        tracked: true,
        displayOnCard: false,
        description: 'Maximum idle current'
      },
      leakageCurrent: {
        name: 'Leakage Current',
        value: null,
        unit: 'µA',
        min: 0,
        max: 1000,
        tracked: true,
        displayOnCard: false,
        description: 'Shutdown/standby leakage current'
      },
      package: {
        name: 'Package',
        value: '',
        unit: '',
        tracked: true,
        displayOnCard: false,
        description: 'Physical package type'
      },
      technology: {
        name: 'Technology',
        value: '',
        unit: '',
        tracked: true,
        displayOnCard: false,
        description: 'Semiconductor process technology'
      },
      dcPower: {
        name: 'DC Power',
        value: 1000,
        unit: 'mW',
        min: 0,
        max: 10000,
        tracked: true,
        displayOnCard: false,
        description: 'DC power consumption'
      }
    }
  },

  // ============================================================================
  // PASSIVE COMPONENTS
  // ============================================================================

  attenuator: {
    type: 'attenuator',
    displayName: 'Attenuator',
    category: 'passive',
    inputs: ['rfIn'],
    outputs: ['rfOut'],
    parameters: {
      attenuation: {
        name: 'Attenuation',
        value: 10,
        unit: 'dB',
        min: 0,
        max: 60,
        tracked: true,
        displayOnCard: true
      },
      vswr: {
        name: 'VSWR',
        value: 1.2,
        unit: '',
        min: 1,
        max: 10,
        tracked: true,
        displayOnCard: false
      },
      powerHandling: {
        name: 'Power Handling',
        value: 30,
        unit: 'dBm',
        min: 0,
        max: 60,
        tracked: true,
        displayOnCard: false
      }
    }
  },

  variableAttenuator: {
    type: 'variableAttenuator',
    displayName: 'Variable Attenuator',
    category: 'passive',
    inputs: ['rfIn'],
    outputs: ['rfOut'],
    parameters: {
      attenuationMin: {
        name: 'Atten Min',
        value: 0,
        unit: 'dB',
        tracked: true,
        displayOnCard: false
      },
      attenuationMax: {
        name: 'Atten Max',
        value: 30,
        unit: 'dB',
        tracked: true,
        displayOnCard: false
      },
      attenuation: {
        name: 'Attenuation',
        value: 10,
        unit: 'dB',
        min: 0,
        max: 30,
        tracked: true,
        displayOnCard: true
      },
      stepSize: {
        name: 'Step Size',
        value: 0.5,
        unit: 'dB',
        tracked: true,
        displayOnCard: false
      },
      vswr: {
        name: 'VSWR',
        value: 1.3,
        unit: '',
        tracked: true,
        displayOnCard: false
      }
    }
  },

  cable: {
    type: 'cable',
    displayName: 'Cable',
    category: 'passive',
    inputs: ['rfIn'],
    outputs: ['rfOut'],
    parameters: {
      length: {
        name: 'Length',
        value: 1,
        unit: 'm',
        min: 0,
        max: 1000,
        tracked: true,
        displayOnCard: true
      },
      lossPerMeter: {
        name: 'Loss/m',
        value: 0.5,
        unit: 'dB/m',
        min: 0,
        max: 10,
        tracked: true,
        displayOnCard: true
      },
      impedance: {
        name: 'Impedance',
        value: 50,
        unit: 'Ω',
        min: 1,
        max: 1000,
        tracked: true,
        displayOnCard: false
      },
      velocityFactor: {
        name: 'Velocity Factor',
        value: 0.66,
        unit: '',
        min: 0.1,
        max: 1,
        tracked: true,
        displayOnCard: false
      }
    }
  },

  splitter: {
    type: 'splitter',
    displayName: 'Splitter',
    category: 'passive',
    inputs: ['rfIn'],
    outputs: ['rfOut1', 'rfOut2'],
    parameters: {
      numWays: {
        name: 'Ways',
        value: 2,
        unit: '',
        min: 2,
        max: 8,
        tracked: true,
        displayOnCard: true,
        description: '2, 3, 4, or 8-way splitter'
      },
      lossPerPort: {
        name: 'Loss/Port',
        value: 3.5,
        unit: 'dB',
        min: 0,
        max: 20,
        tracked: true,
        displayOnCard: true,
        description: 'Insertion loss per output'
      },
      isolation: {
        name: 'Isolation',
        value: 20,
        unit: 'dB',
        min: 0,
        max: 60,
        tracked: true,
        displayOnCard: false,
        description: 'Port-to-port isolation'
      },
      vswr: {
        name: 'VSWR',
        value: 1.3,
        unit: '',
        tracked: true,
        displayOnCard: false
      }
    }
  },

  combiner: {
    type: 'combiner',
    displayName: 'Combiner',
    category: 'passive',
    inputs: ['rfIn1', 'rfIn2'],
    outputs: ['rfOut'],
    parameters: {
      numWays: {
        name: 'Ways',
        value: 2,
        unit: '',
        min: 2,
        max: 8,
        tracked: true,
        displayOnCard: true
      },
      lossPerPort: {
        name: 'Loss/Port',
        value: 3.5,
        unit: 'dB',
        tracked: true,
        displayOnCard: true
      },
      isolation: {
        name: 'Isolation',
        value: 20,
        unit: 'dB',
        tracked: true,
        displayOnCard: false
      }
    }
  },

  // ============================================================================
  // FILTERS
  // ============================================================================

  bandpassFilter: {
    type: 'bandpassFilter',
    displayName: 'Bandpass Filter',
    category: 'filters',
    inputs: ['rfIn'],
    outputs: ['rfOut'],
    parameters: {
      centerFrequency: {
        name: 'Center Freq',
        value: 2400,
        unit: 'MHz',
        min: 0.1,
        max: 100000,
        tracked: true,
        displayOnCard: true
      },
      bandwidth: {
        name: 'Bandwidth',
        value: 100,
        unit: 'MHz',
        min: 0.01,
        max: 10000,
        tracked: true,
        displayOnCard: true
      },
      insertionLoss: {
        name: 'Insertion Loss',
        value: 2,
        unit: 'dB',
        min: 0,
        max: 30,
        tracked: true,
        displayOnCard: true
      },
      stopbandRejection: {
        name: 'Stopband Rejection',
        value: 40,
        unit: 'dB',
        min: 0,
        max: 120,
        tracked: true,
        displayOnCard: false
      },
      vswr: {
        name: 'VSWR',
        value: 1.5,
        unit: '',
        tracked: true,
        displayOnCard: false
      },
      order: {
        name: 'Order',
        value: 5,
        unit: '',
        min: 1,
        max: 20,
        tracked: true,
        displayOnCard: false,
        description: 'Filter order (poles)'
      }
    }
  },

  lowpassFilter: {
    type: 'lowpassFilter',
    displayName: 'Lowpass Filter',
    category: 'filters',
    inputs: ['rfIn'],
    outputs: ['rfOut'],
    parameters: {
      cutoffFrequency: {
        name: 'Cutoff Freq',
        value: 2400,
        unit: 'MHz',
        min: 0.1,
        max: 100000,
        tracked: true,
        displayOnCard: true
      },
      insertionLoss: {
        name: 'Insertion Loss',
        value: 1.5,
        unit: 'dB',
        tracked: true,
        displayOnCard: true
      },
      stopbandRejection: {
        name: 'Stopband Rejection',
        value: 40,
        unit: 'dB',
        tracked: true,
        displayOnCard: false
      },
      rolloffRate: {
        name: 'Rolloff Rate',
        value: 12,
        unit: 'dB/octave',
        tracked: true,
        displayOnCard: false
      }
    }
  },

  highpassFilter: {
    type: 'highpassFilter',
    displayName: 'Highpass Filter',
    category: 'filters',
    inputs: ['rfIn'],
    outputs: ['rfOut'],
    parameters: {
      cutoffFrequency: {
        name: 'Cutoff Freq',
        value: 1000,
        unit: 'MHz',
        tracked: true,
        displayOnCard: true
      },
      insertionLoss: {
        name: 'Insertion Loss',
        value: 1.5,
        unit: 'dB',
        tracked: true,
        displayOnCard: true
      },
      stopbandRejection: {
        name: 'Stopband Rejection',
        value: 40,
        unit: 'dB',
        tracked: true,
        displayOnCard: false
      }
    }
  },

  bandstopFilter: {
    type: 'bandstopFilter',
    displayName: 'Bandstop Filter',
    category: 'filters',
    inputs: ['rfIn'],
    outputs: ['rfOut'],
    parameters: {
      centerFrequency: {
        name: 'Center Freq',
        value: 2400,
        unit: 'MHz',
        tracked: true,
        displayOnCard: true
      },
      bandwidth: {
        name: 'Bandwidth',
        value: 50,
        unit: 'MHz',
        tracked: true,
        displayOnCard: true
      },
      stopbandAttenuation: {
        name: 'Stopband Atten',
        value: 40,
        unit: 'dB',
        tracked: true,
        displayOnCard: true
      },
      insertionLoss: {
        name: 'Insertion Loss',
        value: 1,
        unit: 'dB',
        tracked: true,
        displayOnCard: false
      }
    }
  },

  // ============================================================================
  // MIXERS
  // ============================================================================

  passiveMixer: {
    type: 'passiveMixer',
    displayName: 'Passive Mixer',
    category: 'mixers',
    inputs: ['rfIn', 'loIn'],
    outputs: ['ifOut'],
    parameters: {
      conversionLoss: {
        name: 'Conversion Loss',
        value: 7,
        unit: 'dB',
        min: 0,
        max: 20,
        tracked: true,
        displayOnCard: true
      },
      loDrive: {
        name: 'LO Drive',
        value: 7,
        unit: 'dBm',
        min: -10,
        max: 30,
        tracked: true,
        displayOnCard: true
      },
      iip3: {
        name: 'IIP3',
        value: 10,
        unit: 'dBm',
        tracked: true,
        displayOnCard: false
      },
      loRfIsolation: {
        name: 'LO-RF Isolation',
        value: 20,
        unit: 'dB',
        tracked: true,
        displayOnCard: false
      },
      loIfIsolation: {
        name: 'LO-IF Isolation',
        value: 20,
        unit: 'dB',
        tracked: true,
        displayOnCard: false
      },
      rfIfIsolation: {
        name: 'RF-IF Isolation',
        value: 20,
        unit: 'dB',
        tracked: true,
        displayOnCard: false
      },
      noiseFigure: {
        name: 'NF',
        value: 7,
        unit: 'dB',
        tracked: true,
        displayOnCard: true
      }
    }
  },

  activeMixer: {
    type: 'activeMixer',
    displayName: 'Active Mixer',
    category: 'mixers',
    inputs: ['rfIn', 'loIn'],
    outputs: ['ifOut'],
    parameters: {
      conversionGain: {
        name: 'Conversion Gain',
        value: 8,
        unit: 'dB',
        min: -10,
        max: 30,
        tracked: true,
        displayOnCard: true
      },
      loDrive: {
        name: 'LO Drive',
        value: 0,
        unit: 'dBm',
        tracked: true,
        displayOnCard: true
      },
      noiseFigure: {
        name: 'NF',
        value: 12,
        unit: 'dB',
        tracked: true,
        displayOnCard: true
      },
      iip3: {
        name: 'IIP3',
        value: 0,
        unit: 'dBm',
        tracked: true,
        displayOnCard: false
      },
      p1db: {
        name: 'OP1dB',
        value: -5,
        unit: 'dBm',
        tracked: true,
        displayOnCard: false
      }
    }
  },

  // ============================================================================
  // SWITCHES
  // ============================================================================

  spdtSwitch: {
    type: 'spdtSwitch',
    displayName: 'SPDT Switch',
    category: 'switches',
    inputs: ['rfIn'],
    outputs: ['rfOut1', 'rfOut2'],
    parameters: {
      insertionLoss: {
        name: 'Insertion Loss',
        value: 0.5,
        unit: 'dB',
        tracked: true,
        displayOnCard: true
      },
      isolation: {
        name: 'Isolation',
        value: 40,
        unit: 'dB',
        min: 0,
        max: 100,
        tracked: true,
        displayOnCard: true
      },
      switchingTime: {
        name: 'Switching Time',
        value: 100,
        unit: 'ns',
        tracked: true,
        displayOnCard: false
      },
      vswr: {
        name: 'VSWR',
        value: 1.3,
        unit: '',
        tracked: true,
        displayOnCard: false
      },
      powerHandling: {
        name: 'Power Handling',
        value: 35,
        unit: 'dBm',
        tracked: true,
        displayOnCard: false
      },
      activePort: {
        name: 'Active Port',
        value: 1,
        unit: '',
        min: 1,
        max: 2,
        tracked: true,
        displayOnCard: true,
        description: 'Which output port is active'
      }
    }
  },

  sp4tSwitch: {
    type: 'sp4tSwitch',
    displayName: 'SP4T Switch',
    category: 'switches',
    inputs: ['rfIn'],
    outputs: ['rfOut1', 'rfOut2', 'rfOut3', 'rfOut4'],
    parameters: {
      insertionLoss: {
        name: 'Insertion Loss',
        value: 1,
        unit: 'dB',
        tracked: true,
        displayOnCard: true
      },
      isolation: {
        name: 'Isolation',
        value: 35,
        unit: 'dB',
        tracked: true,
        displayOnCard: true
      },
      switchingTime: {
        name: 'Switching Time',
        value: 150,
        unit: 'ns',
        tracked: true,
        displayOnCard: false
      },
      activePort: {
        name: 'Active Port',
        value: 1,
        unit: '',
        min: 1,
        max: 4,
        tracked: true,
        displayOnCard: true
      }
    }
  },

  // ============================================================================
  // SYSTEM COMPONENTS
  // ============================================================================

  termination: {
    type: 'termination',
    displayName: 'Termination',
    category: 'system',
    inputs: ['rfIn'],
    outputs: [],
    parameters: {
      impedance: {
        name: 'Impedance',
        value: 50,
        unit: 'Ω',
        tracked: true,
        displayOnCard: true
      },
      vswr: {
        name: 'VSWR',
        value: 1.1,
        unit: '',
        tracked: true,
        displayOnCard: false
      },
      powerHandling: {
        name: 'Power Handling',
        value: 30,
        unit: 'dBm',
        tracked: true,
        displayOnCard: false
      }
    }
  },

  generic: {
    type: 'generic',
    displayName: 'Generic',
    category: 'system',
    inputs: ['rfIn'],
    outputs: ['rfOut'],
    parameters: {
      gain: {
        name: 'Gain',
        value: 0,
        unit: 'dB',
        tracked: true,
        displayOnCard: true
      },
      noiseFigure: {
        name: 'NF',
        value: 0,
        unit: 'dB',
        tracked: true,
        displayOnCard: true
      },
      p1db: {
        name: 'OP1dB',
        value: 30,
        unit: 'dBm',
        tracked: true,
        displayOnCard: false
      }
    }
  },

  note: {
    type: 'note',
    displayName: 'Note',
    category: 'system',
    inputs: [],
    outputs: [],
    parameters: {
      text: {
        name: 'Note Text',
        value: 'Add your notes here...',
        unit: '',
        tracked: false,
        displayOnCard: true
      },
      color: {
        name: 'Color',
        value: 'yellow',
        unit: '',
        tracked: false,
        displayOnCard: false,
        options: ['yellow', 'orange', 'red', 'cyan']
      }
    }
  },

  // ============================================================================
  // MEASUREMENT
  // ============================================================================

  measurement: {
    type: 'measurement',
    displayName: 'Measurement',
    category: 'measurement',
    inputs: ['rfIn'],
    outputs: ['rfOut'],
    parameters: {
      gain: {
        name: 'Gain',
        value: 0,
        unit: 'dB',
        min: -50,
        max: 50,
        tracked: true,
        displayOnCard: false,
        description: 'Passthrough gain (typically 0 dB)'
      },
      noiseFigure: {
        name: 'NF',
        value: 0,
        unit: 'dB',
        min: 0,
        max: 50,
        tracked: true,
        displayOnCard: false,
        description: 'Passthrough noise figure (typically 0 dB)'
      }
    }
  }
};

export const COMPONENT_CATEGORIES = {
  sources: {
    name: 'Sources',
    components: ['inputSource', 'noiseSource']
  },
  amplifiers: {
    name: 'Amplifiers',
    components: ['lna', 'powerAmplifier', 'vga', 'gainBlock', 'driver']
  },
  passive: {
    name: 'Passive',
    components: ['attenuator', 'variableAttenuator', 'cable', 'splitter', 'combiner']
  },
  filters: {
    name: 'Filters',
    components: ['bandpassFilter', 'lowpassFilter', 'highpassFilter', 'bandstopFilter']
  },
  mixers: {
    name: 'Mixers',
    components: ['passiveMixer', 'activeMixer']
  },
  switches: {
    name: 'Switches',
    components: ['spdtSwitch', 'sp4tSwitch']
  },
  measurement: {
    name: 'Measurement',
    components: ['measurement']
  },
  system: {
    name: 'System',
    components: ['termination', 'generic', 'note']
  }
};

// Inject data source parameters into supported components
const enhancedLibrary = {};
Object.keys(COMPONENT_LIBRARY).forEach(key => {
  enhancedLibrary[key] = injectDataSourceParameters(COMPONENT_LIBRARY[key]);
});

export default enhancedLibrary;
export { COMPONENT_LIBRARY as COMPONENT_LIBRARY_RAW };
